import React from 'react';
import { LeaderboardEntry } from '../utils/apiClient';

interface LeaderboardModalProps {
  entries: LeaderboardEntry[];
  loading?: boolean;
  error?: string | null;
  /** Optional final score to highlight (shown after a completed run). */
  finalScore?: number | null;
  /** Primary action label (e.g. "Start" or "Play Again"). */
  primaryLabel: string;
  onPrimary: () => void;
  /** Secondary action (e.g. switch back to another mode). */
  secondaryLabel?: string;
  onSecondary?: () => void;
}

/**
 * Splash/modal showing the top competitive scores. Displayed when entering
 * Competitive mode and again after a run ends (with the final score).
 */
const LeaderboardModal: React.FC<LeaderboardModalProps> = ({
  entries,
  loading = false,
  error = null,
  finalScore = null,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
}) => {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'min(20px, 4vw)',
      }}
    >
      <div
        style={{
          backgroundColor: '#1e1e1e',
          borderRadius: 'min(16px, 3.2vw)',
          padding: 'min(28px, 5.6vw)',
          width: 'min(420px, 92vw)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          textAlign: 'center',
        }}
      >
        <h2 style={{ fontSize: 'min(1.8rem, 7vw)', marginBottom: 'min(8px, 1.6vw)' }}>
          🏆 Leaderboard
        </h2>

        {finalScore !== null && (
          <p
            style={{
              fontSize: 'min(1.2rem, 4.8vw)',
              color: '#facc15',
              marginBottom: 'min(16px, 3.2vw)',
            }}
          >
            Your score: <strong>{finalScore.toLocaleString()}</strong>
          </p>
        )}

        <div style={{ minHeight: '6em', marginBottom: 'min(20px, 4vw)' }}>
          {loading && <p style={{ color: '#888' }}>Loading…</p>}
          {error && !loading && <p style={{ color: '#f87171' }}>{error}</p>}
          {!loading && !error && entries.length === 0 && (
            <p style={{ color: '#888' }}>No scores yet — be the first!</p>
          )}
          {!loading && !error && entries.length > 0 && (
            <ol style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
              {entries.map((entry, index) => (
                <li
                  key={`${entry.username}-${entry.created_at}-${index}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'min(10px, 2vw) min(12px, 2.4vw)',
                    borderRadius: 'min(8px, 1.6vw)',
                    backgroundColor: index % 2 === 0 ? '#262626' : 'transparent',
                  }}
                >
                  <span style={{ display: 'flex', gap: 'min(10px, 2vw)', minWidth: 0 }}>
                    <span style={{ color: '#888', width: '1.5em' }}>{index + 1}.</span>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.username}
                    </span>
                  </span>
                  <strong style={{ color: '#3b82f6' }}>{entry.points.toLocaleString()}</strong>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div style={{ display: 'flex', gap: 'min(12px, 2.4vw)', justifyContent: 'center' }}>
          {secondaryLabel && onSecondary && (
            <button
              onClick={onSecondary}
              style={{
                flex: 1,
                padding: 'min(12px, 2.4vw)',
                fontSize: 'min(1.1rem, 4.4vw)',
                backgroundColor: 'transparent',
                color: '#ccc',
                border: '1px solid #444',
                borderRadius: 'min(8px, 1.6vw)',
                cursor: 'pointer',
              }}
            >
              {secondaryLabel}
            </button>
          )}
          <button
            onClick={onPrimary}
            style={{
              flex: 1,
              padding: 'min(12px, 2.4vw)',
              fontSize: 'min(1.1rem, 4.4vw)',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: 'min(8px, 1.6vw)',
              cursor: 'pointer',
            }}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardModal;
